/* eslint-disable @typescript-eslint/no-throw-literal */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-nested-ternary */
import { isNullOrUndefined } from 'util';

import React, { useContext, useEffect, useState } from 'react';

import { Switch } from '@headlessui/react';
import { useLogSnag } from '@logsnag/next';
import axios from 'axios';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  runTransaction,
} from 'firebase/firestore';
import Link from 'next/link';
import router from 'next/router';
import Confetti from 'react-confetti';
import { BsCash } from 'react-icons/bs';
import {
  FaChevronDown,
  FaChevronUp,
  FaGolfBall,
  FaRegEnvelope,
} from 'react-icons/fa';
import { toast } from 'react-toastify';

import config from '../config/index.json';
import { UserContext } from '../lib/context';
import { getCustomerStripe } from '../stripe/getStripe';
import { Bracket } from '../types/Bracket';
import { EventType } from '../types/EventType';
import { MultiRoundTournament } from '../types/MultiRoundTournament';
import { Player } from '../types/Player';
import { Registrant } from '../types/Registrant';
import {
  Registration,
  RegistrationQuestion,
  RegistrationQuestionType,
} from '../types/Registration';
import { RegistrationPrice } from '../types/RegistrationPrice';
import { RosterMember } from '../types/RosterMember';
import { RoundRobin } from '../types/RoundRobin';
import { RyderCup } from '../types/RyderCup';
import { Tournament } from '../types/Tournament';
import { UserDoc } from '../types/UserDoc';
import { TOKEN_PATH, USGA_BASE_URL } from '../utils/constants';
import {
  formatDateAndTimeToInputFormat,
  generateRegistrationID,
  logEventInternal,
  maybeMoveWaitlistToRegistrants,
  preserveUndefined,
} from '../utils/utils';
import LoadingSpinner from './LoadingSpinner';
import SearchBar from './SearchBar';
import ViewRegistrations from './ViewRegistrations';

type RegistrationCardProps = {
  event: Tournament | MultiRoundTournament | RoundRobin | Bracket | RyderCup;
  registration?: string;
};

export default function RegistrationCard({
  event,
  registration = '',
}: RegistrationCardProps) {
  const { actingUserId }: any = useContext(UserContext);

  const alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('');
  const { company } = config;
  const { track } = useLogSnag();
  const { logo } = company;

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalContent, setModalContent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGHIN, setIsGHIN] = useState<boolean>(true);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [handicap, setHandicap] = useState<string>(
    event.registration?.isNonGolfEvent ? '0' : ''
  );
  const [selectedPrice, setSelectedPrice] = useState<RegistrationPrice>(
    event.registration?.prices?.[0] ?? { fee: 0, name: '' }
  );

  const [loading, setLoading] = useState<boolean>(false);

  const [showTeeTimeDropdown, setShowTeeTimeDropdown] =
    useState<boolean>(false);

  const [stripePercentage, setStripePercentage] = useState<number>(0.951); // 2.9% + 2% for Live Tourney
  const [applicationFeePercentage, setApplicationFeePercentage] =
    useState<number>(0.02);

  const [availablePlayers, setAvailablePlayers] = useState<RosterMember[]>([]);

  const DEFUALT_MODAL_BUTTON = (
    <button
      onClick={() => {
        setShowModal(false);
      }}
      type="button"
      className="mb-2 w-48 float-right inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gray-400 text-base font-medium text-white hover:bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tertiary sm:ml-3 sm:w-auto sm:text-sm"
    >
      Close
    </button>
  );

  const USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const [modalButton, setModalButton] = useState<any>(DEFUALT_MODAL_BUTTON);

  function modalClick(e: any) {
    e.stopPropagation();
    return false;
  }

  const modal = (
    <div
      className="relative z-30"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

      <div
        onClick={() => {
          setShowModal(false);
        }}
        className="fixed z-10 inset-0 overflow-y-auto"
      >
        <div
          className={`flex sm:w-full items-end sm:items-center justify-center p-4 text-center sm:p-0`}
        >
          <div
            onClick={(e) => modalClick(e)}
            className={`relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8`}
          >
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {modalTitle}
                  </h3>
                  <div className="mt-2">{modalContent}</div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              {modalButton}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  let registrant = event.registration?.registrants?.find(
    (reg: Registrant) => reg.id === registration
  ) as Registrant | null;

  if (!registrant) {
    registrant = (event.registration?.waitlistRegistrants ?? []).find(
      (reg: Registrant) => reg.id === registration
    ) as Registrant | null;
  }

  if (!registrant) {
    registrant = null;
  }

  const [currentRegistrant, setCurrentRegistrant] = useState<Registrant | null>(
    registrant
  );

  const registrationOpen =
    new Date(event.registration?.registrationCutOff ?? '') > new Date() &&
    (event.registration?.registrationStart == null
      ? true
      : new Date(event.registration?.registrationStart ?? '') < new Date()) &&
    event.registration?.registrationOpen &&
    (event.registration?.registrants
      ?.filter(
        (val: Registrant) =>
          isNullOrUndefined(val.paymentStatus) || val.paymentStatus === 'paid'
      )
      .flatMap((r: Registrant) => r.players).length ?? 0) <
      event.registration?.maxPlayerCount;
  const [ghin, setGhin] = useState<string>('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [players, setPlayers] = useState<Player[]>(
    registrant !== null ? registrant.players : []
  );
  const [answers, setAnswers] = useState<string[]>(
    registrant !== null
      ? registrant.answers
      : event.registration?.questions?.map((question: RegistrationQuestion) =>
          question.type === RegistrationQuestionType.MultipleChoice
            ? '' // Initialize multiple choice answers as empty string
            : question.teeTimes?.[0]?.time ?? ''
        ) ?? []
  );
  const [ghinToken, setGhinToken] = useState<string>('');

  const registrantsLengths = event.registration?.registrants
    ?.filter(
      (val: Registrant) =>
        isNullOrUndefined(val.paymentStatus) || val.paymentStatus === 'paid'
    )
    .map((val: Registrant) => val.players.length);
  const playerCount = registrantsLengths?.reduce(
    (a: number, b: number) => a + b,
    0
  );

  const [updatingRegistration, setUpdatingRegistration] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // prompt the user if they try and leave with unsaved changes
  useEffect(() => {
    const warningText =
      'You have not registered yet - are you sure you want to leave this page?';
    const handleWindowClose = (e: BeforeUnloadEvent) => {
      if (!unsavedChanges) return;
      e.preventDefault();
      e.returnValue = warningText;
      return warningText;
    };
    const handleBrowseAway = () => {
      if (!unsavedChanges) return;
      if (window.confirm(warningText)) return;
      router.events.emit('routeChangeError');
      throw 'routeChange aborted.';
    };
    window.addEventListener('beforeunload', handleWindowClose);
    router.events.on('routeChangeStart', handleBrowseAway);
    return () => {
      window.removeEventListener('beforeunload', handleWindowClose);
      router.events.off('routeChangeStart', handleBrowseAway);
    };
  }, [unsavedChanges]);

  useEffect(() => {
    const fetchUserData = async () => {
      const tokensRef = doc(getFirestore(), 'ghintokens', TOKEN_PATH);
      const tokenDoc = await getDoc(tokensRef);
      setGhinToken(tokenDoc?.data()?.token);

      const userRef = doc(
        getFirestore(),
        'users',
        event.createdBy ?? actingUserId
      );
      onSnapshot(userRef, (userDoc) => {
        const user = userDoc.data() as UserDoc;
        if (user?.negotiatedRegistrationFee != null) {
          if (
            user.negotiatedRegistrationFee < 0 ||
            user.negotiatedRegistrationFee > 0.05
          ) {
            track({
              channel: 'registration',
              event: 'Negotiated Fee Out of Range',
              icon: '❌',
              tags: {
                event_name: event.name,
                event_creator: user?.email ?? '',
                negotiated_fee: user.negotiatedRegistrationFee,
              },
            });
          } else {
            setStripePercentage(0.971 - user.negotiatedRegistrationFee);
            setApplicationFeePercentage(user.negotiatedRegistrationFee);
          }
        }

        if ((event.registration?.clubs?.length ?? 0) > 0) {
          setAvailablePlayers(
            userDoc?.data()?.roster.filter((val: RosterMember) => {
              // check if any of val.clubs is in event.registration?.clubs
              return val.clubs.some((club) =>
                event.registration?.clubs?.includes(club)
              );
            }) ?? []
          );
        }
      });
    };
    fetchUserData();
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  async function addPlayer() {
    if (
      email === '' ||
      !String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      setError('Please enter an valid email address.');
      return;
    }
    if (isGHIN && ghin !== '') {
      if (
        players.find((player) => player.ghin === parseInt(ghin, 10)) ||
        event.registration?.registrants
          ?.filter(
            (val: Registrant) =>
              isNullOrUndefined(val.paymentStatus) ||
              val.paymentStatus === 'paid'
          )
          .find((r: Registrant) =>
            r.players.find(
              (player: Player) => player.ghin === parseInt(ghin, 10)
            )
          )
      ) {
        setError('Oops! That player is already registered for this event.');
        return;
      }

      if (parseInt(ghin, 10) < 40) {
        setError('Please enter a valid GHIN #');
        return;
      }
      const response = await fetch(
        `${USGA_BASE_URL}/golfers.json?status=Active&from_ghin=true&per_page=25&page=1&golfer_id=${ghin}&source=GHIN.com`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${ghinToken}`,
          },
        }
      );

      // If you care about a response:
      if (response.body !== null) {
        const result: any = await response.json();
        if (!isNullOrUndefined(result?.golfers) && result.golfers.length > 0) {
          if (result?.golfers[0] !== null && result?.golfers[0] !== undefined) {
            let index = result?.golfers[0].handicap_index as string;
            if (index.charAt(0) === '+') {
              index = `-${index.substring(1)}`;
            } else if (index === 'NH') {
              index = '0';
            }

            const player: Player = {
              firstName: result?.golfers[0].first_name,
              lastName: result?.golfers[0].last_name,
              handicap: parseFloat(index),
              ghin: parseInt(ghin, 10),
              email,
              registrationPrice: selectedPrice,
            };
            setPlayers([...players, player]);
            setGhin('');
            setError('');
            setEmail('');
          } else {
            setError('GHIN # Not Found');
          }
        } else {
          setError('GHIN # Not Found');
        }
      } else {
        setError('GHIN # Not Found');
      }
      //
    } else {
      if (Number.isNaN(parseFloat(handicap))) {
        setError('Handicap must be a number');
        return;
      }

      if (firstName === '' || lastName === '') {
        setError('First and Last Name are required');
        return;
      }

      if (parseFloat(handicap) > 100 || parseFloat(handicap) < -100) {
        setError(
          'Handicap must be a valid number. To Enter a GHIN, use the GHIN option.'
        );
        return;
      }
      // Creating Player Manually
      if (
        players.find(
          (player) =>
            player.firstName === firstName &&
            player.lastName === lastName &&
            player.handicap === parseFloat(handicap)
        ) ||
        event.registration?.registrants
          ?.filter(
            (val: Registrant) =>
              isNullOrUndefined(val.paymentStatus) ||
              val.paymentStatus === 'paid'
          )
          .find((r: Registrant) =>
            r.players.find(
              (player: Player) =>
                player.firstName === firstName &&
                player.lastName === lastName &&
                player.handicap === parseFloat(handicap)
            )
          )
      ) {
        setError('Oops! That person is already registered for this event.');
      }

      let handicapValue = parseFloat(handicap);

      if (handicap.charAt(0) === '+') {
        handicapValue *= -1;
      }

      const player: Player = {
        firstName,
        lastName,
        handicap: handicapValue,
        email,
        registrationPrice: selectedPrice,
      };

      setPlayers([...players, player]);
      setFirstName('');
      setLastName('');
      setHandicap(event.registration?.isNonGolfEvent ? '0' : '');
      setError('');
      setEmail('');
    }
    setUnsavedChanges(true);
  }

  async function sendConfirmationEmail(
    reservationId: string,
    isWaitlist: boolean = false
  ) {
    const eventUrl =
      event.registration?.isMultiRound ?? false
        ? `https://livetourney.com/event/${event.eventCode}/?registration=${reservationId}`
        : event.registration?.isBracket ?? false
        ? `https://livetourney.com/bracket/${event.eventCode}/?registration=${reservationId}`
        : event.registration?.isRoundRobin ?? false
        ? `https://livetourney.com/roundRobin/${event.eventCode}/?registration=${reservationId}`
        : `https://livetourney.com/${event.eventCode}/?registration=${reservationId}`;

    let emailBody = `You have successfully registered for ${event.name}! To view, update, or cancel your reservation, please visit the <a href='${eventUrl}'>event portal</a>.`;

    if (isWaitlist) {
      emailBody = `You have been added to the waitlist for ${event.name}. You will be informed by the event organizer if a spot opens up. Please contact the event organzier for more information. To view, update, or cancel your waitlist entry, please visit the <a href='${eventUrl}'>event portal</a>`;
    }

    // create a section where we add the players who are part of this registration
    emailBody += '<p><h3>Players</h3>';
    players.forEach((player) => {
      emailBody += `<p>${player.firstName} ${player.lastName}</p>`;
    });
    emailBody += '</p>';

    // create a section where we add the questions and the players answers to the email
    if ((event.registration?.questions?.length ?? 0) > 0) {
      emailBody += '<p><h3>Questions</h3>';
      event.registration?.questions?.forEach((question, i) => {
        emailBody += `<p>${question.question}: ${answers[i]}</p>`;
      });
      emailBody += '</p>';
    }

    const emailHTML = `<table role="presentation" style="width:100%;border-collapse:collapse;border:0;border-spacing:0;background:#ffffff;">
    <tr>
      <td align="center" style="padding:0;">
        <table role="presentation" style="width:100%;border-collapse:collapse;border-spacing:0;text-align:left;">
          <tr>
            <td align="center" style="padding:40px 0 30px 0;background:#14573b;">
              <img src="http://cdn.mcauto-images-production.sendgrid.net/dfbc5719d6176728/5a07346f-68ea-4b09-8417-0b508b65bd1a/1112x212.png" alt="" width="300" style="height:auto;display:block;" />
            </td>
          </tr>
          <tr>
            <td style="padding:36px 30px 42px 30px;">
              <table role="presentation" style="width:100%;border-collapse:collapse;border:0;border-spacing:0;">
                <tr>
                  <td style="padding:0 0 36px 0;color:#153643;">
                    <p style="margin:0 0 12px 0;font-size:16px;line-height:24px;font-family:Arial,sans-serif;color:#153643;">${emailBody}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:30px;background:#c5c5c5;">
              <table role="presentation" style="width:100%;border-collapse:collapse;border:0;border-spacing:0;font-size:9px;font-family:Arial,sans-serif;">
                <tr>
                  <td style="padding:0;width:50%;" align="left">
                    <p style="margin:0;font-size:14px;line-height:16px;font-family:Arial,sans-serif;color:#14573b;">
                      &reg; Live Tourney, 2023<br/><a href="https://www.livetourney.com/unsubscribe" style="color:#14573b;text-decoration:underline;">Unsubscribe</a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>`;

    const emailObject = {
      to: players.map((player) => player.email),
      from: `Live Tourney Registration <noreply@livetourney.com>`,
      replyTo: event.creatorEmail ?? 'help@livetourney.com',
      message: {
        subject: `${isWaitlist ? 'Waitlist' : 'Registration'} Confirmation - ${
          event.name
        }`,
        html: emailHTML,
      },
      tournament: event.id,
      tournamentName: event.name,
    };

    await addDoc(collection(getFirestore(), 'mail'), {
      ...emailObject,
    });
  }

  async function sendCancellationEmail() {
    const eventUrl =
      event.registration?.isMultiRound ?? false
        ? `https://livetourney.com/event/${event.eventCode}`
        : event.registration?.isBracket ?? false
        ? `https://livetourney.com/bracket/${event.eventCode}`
        : event.registration?.isRoundRobin ?? false
        ? `https://livetourney.com/roundRobin/${event.eventCode}`
        : `https://livetourney.com/${event.eventCode}`;

    const emailBody = `Your registration for ${event.name} has been cancelled. If this was a mistake, please visit the <a href='${eventUrl}'>event portal</a>.`;

    const emailHTML = `<table role="presentation" style="width:100%;border-collapse:collapse;border:0;border-spacing:0;background:#ffffff;">
    <tr>
      <td align="center" style="padding:0;">
        <table role="presentation" style="width:100%;border-collapse:collapse;border-spacing:0;text-align:left;">
          <tr>
            <td align="center" style="padding:40px 0 30px 0;background:#14573b;">
              <img src="http://cdn.mcauto-images-production.sendgrid.net/dfbc5719d6176728/5a07346f-68ea-4b09-8417-0b508b65bd1a/1112x212.png" alt="" width="300" style="height:auto;display:block;" />
            </td>
          </tr>
          <tr>
            <td style="padding:36px 30px 42px 30px;">
              <table role="presentation" style="width:100%;border-collapse:collapse;border:0;border-spacing:0;">
                <tr>
                  <td style="padding:0 0 36px 0;color:#153643;">
                    <p style="margin:0 0 12px 0;font-size:16px;line-height:24px;font-family:Arial,sans-serif;color:#153643;">${emailBody}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:30px;background:#c5c5c5;">
              <table role="presentation" style="width:100%;border-collapse:collapse;border:0;border-spacing:0;font-size:9px;font-family:Arial,sans-serif;">
                <tr>
                  <td style="padding:0;width:50%;" align="left">
                    <p style="margin:0;font-size:14px;line-height:16px;font-family:Arial,sans-serif;color:#14573b;">
                      &reg; Live Tourney, 2023<br/><a href="https://www.livetourney.com/unsubscribe" style="color:#14573b;text-decoration:underline;">Unsubscribe</a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>`;

    const emailObject = {
      to: players.map((player) => player.email),
      from: `Live Tourney Registration <noreply@livetourney.com>`,
      replyTo: event.creatorEmail ?? 'help@livetourney.com',
      message: {
        subject: 'Registration Cancelled',
        html: emailHTML,
      },
      tournament: event.id,
      tournamentName: event.name,
    };

    await addDoc(collection(getFirestore(), 'mail'), {
      ...emailObject,
    });
  }

  function getEventRef() {
    return doc(
      getFirestore(),
      event.registration?.isMultiRound ?? false
        ? EventType.MultiRoundTournament
        : event.registration?.isBracket ?? false
        ? EventType.Bracket
        : event.registration?.isRoundRobin ?? false
        ? EventType.RoundRobin
        : event.registration?.isRyderCup ?? false
        ? EventType.RyderCup
        : EventType.Tournament,
      event.id
    );
  }

  function calculateAvailableSlots(question: RegistrationQuestion) {
    const slotsMap: { [key: string]: number } = {};

    question.teeTimes?.forEach((teeTime) => {
      slotsMap[teeTime.time] = teeTime.slots;
    });

    event.registration?.registrants?.forEach((r: Registrant) => {
      // TODO: this is wrong. Need to check the index before decrementing to make sure it's the right question
      r.answers.forEach((answer) => {
        if (
          question.type === RegistrationQuestionType.TeeTime &&
          answer &&
          (slotsMap[answer] ?? 0) > 0
        ) {
          slotsMap[answer] = (slotsMap[answer] ?? 0) - r.players.length;
        }
      });
    });

    return slotsMap;
  }

  async function cancelRegistration() {
    if (currentRegistrant !== null) {
      await runTransaction(getFirestore(), async (transaction) => {
        const eventRef = getEventRef();

        const currentEvent = await transaction.get(eventRef);

        const updatedRegistration = {
          ...currentEvent.data()?.registration,
        } as Registration;

        updatedRegistration.registrants =
          updatedRegistration.registrants?.filter(
            (val: Registrant) => val.id !== currentRegistrant.id
          );

        updatedRegistration.waitlistRegistrants =
          updatedRegistration.waitlistRegistrants != null
            ? updatedRegistration.waitlistRegistrants.filter(
                (val: Registrant) => val.id !== currentRegistrant.id
              )
            : [];

        transaction.update(eventRef, 'registration', updatedRegistration);
      });

      const playerNames: any = {};
      currentRegistrant.players.forEach((player, i) => {
        playerNames[
          `player_${alphabet[i]}`
        ] = `${player.firstName} ${player.lastName}`;
        playerNames[`player_${alphabet[i]}_email`] = player.email;
      });
      const isFreeRegistration =
        (event.registration?.prices?.reduce((acc, curr) => acc + curr.fee, 0) ??
          0) === 0;

      // IF this is a free registration, move the next person from the waitlist to the registrants list
      if (currentRegistrant.checkoutSessionId == null && isFreeRegistration) {
        await maybeMoveWaitlistToRegistrants(event);
      }

      track({
        channel: 'registration',
        event: 'Registration Cancelled',
        icon: '❌',
        tags: {
          event_name: event.name,
          event_creator: event.creatorEmail,
          ...playerNames,
        },
      });
      setCurrentRegistrant(null);
      setPlayers([]);
      setAnswers([]);
      alert('Your registration has been cancelled.');
      sendCancellationEmail();
    }
  }

  function validateTeeTimeAvailability(updatedRegistration: Registration) {
    const teeTimeQuestionIndex = updatedRegistration.questions.findIndex(
      (q) => q.type === RegistrationQuestionType.TeeTime
    );
    if (teeTimeQuestionIndex !== -1) {
      const teeTimeQuestion = updatedRegistration.questions[
        teeTimeQuestionIndex
      ] as RegistrationQuestion;
      const selectedTeeTime = answers[teeTimeQuestionIndex] ?? '';
      const availableSlots = calculateAvailableSlots(teeTimeQuestion);
      if ((availableSlots[selectedTeeTime] ?? 0) < players.length) {
        throw new Error(
          'Selected tee time is no longer available. Please choose another.'
        );
      }
    }
  }

  async function collectPayments() {
    if (
      answers.some((answer: string, i: number) => {
        const question = event.registration?.questions?.[i];
        if (
          question?.required ||
          question?.type === RegistrationQuestionType.MultipleChoice
        ) {
          return answer === '';
        }
        return false;
      })
    ) {
      toast.error(
        'Please answer all required questions and select an option for all multiple choice questions.'
      );
      return;
    }
    setIsLoading(true);
    setUnsavedChanges(false);

    const stripeData = await runTransaction(
      getFirestore(),
      async (transaction) => {
        const eventRef = getEventRef();

        const currentEvent = await transaction.get(eventRef);
        const updatedRegistration = {
          ...currentEvent.data()?.registration,
        } as Registration;
        const registrationId = currentRegistrant
          ? currentRegistrant.id
          : generateRegistrationID(event.registration as Registration);

        // Validate tee time availability
        try {
          validateTeeTimeAvailability(updatedRegistration);
        } catch (e: any) {
          toast.error(e.message);
          setIsLoading(false);
          return { error: { message: e.message } };
        }

        let totalRegistrationPrice = 0;
        players.forEach((player: Player) => {
          const playerPrice = player.registrationPrice?.fee ?? 0;

          totalRegistrationPrice += Math.round(
            !event.registration?.feePaidByPlayer
              ? playerPrice * 100
              : ((playerPrice + 0.3) / stripePercentage) * 100
          );
        });

        // Create Stripe checkout
        const { data } = await axios.post(
          '/api/createRegistrationPaymentSession',
          {
            price: Math.round(totalRegistrationPrice),
            success_url: window.location.href,
            cancel_url: window.location.href,
            payoutAccountId: event.registration?.payoutAccountId,
            players: players
              .map((player) => `${player.firstName} ${player.lastName}`)
              .join(', '),
            registrationId,
            eventName: event.name,
            eventCode: event.eventCode,
            isMultiRound: event.registration?.isMultiRound ?? false,
            isBracket: event.registration?.isBracket ?? false,
            isRoundRobin: event.registration?.isRoundRobin ?? false,
            eventId: event.id,
            applicationFeePercent: applicationFeePercentage,
            isWaitlist: !registrationOpen,
            creatorEmail: event.creatorEmail ?? 'N/A',
          }
        );

        const newRegistrant: Registrant = {
          players,
          answers,
          id: registrationId,
          time: new Date().toISOString(),
          checkoutSessionId: data.id,
          paymentStatus: 'pending',
        };
        updatedRegistration.registrants.push(newRegistrant);
        setCurrentRegistrant(newRegistrant);

        transaction.update(eventRef, 'registration', updatedRegistration);
        return data;
      }
    );

    if (stripeData.error) {
      setError(stripeData.error.message);
      setIsLoading(false);
    } else {
      // Redirect to checkout
      const stripe = await getCustomerStripe(
        event.registration?.payoutAccountId ?? ''
      );
      if (stripe) {
        logEventInternal('redirect_to_registration_checkout', {
          event: event.eventCode,
          url: window.location.href,
          players: players
            .map((player) => `${player.firstName} ${player.lastName}`)
            .join(', '),
        });
        const playerNames: any = {};
        players.forEach((player, i) => {
          playerNames[
            `player_${alphabet[i]}`
          ] = `${player.firstName} ${player.lastName}`;
          playerNames[`player_${alphabet[i]}_email`] = player.email;
        });
        track({
          channel: 'registration',
          event: 'Collect Payment Clicked',
          icon: '💸',
          tags: {
            event_name: event.name,
            price: stripeData.amount_total / 100,
            ...playerNames,
            event_creator: event.creatorEmail,
          },
        });
        await stripe.redirectToCheckout({ sessionId: stripeData.id });
      } else {
        setError('Stripe is not loaded.');
        setIsLoading(false);
      }
    }
  }

  async function addToWaitlist() {
    if (
      answers.length !== event.registration?.questions?.length ||
      answers.some((a) => a === '')
    ) {
      toast.error('Please answer all questions.');
      return;
    }
    const returnedRegistrationID = await runTransaction(
      getFirestore(),
      async (transaction) => {
        const eventRef = getEventRef();

        const currentEvent = await transaction.get(eventRef);
        const updatedRegistration = {
          ...currentEvent.data()?.registration,
        } as Registration;

        const registrationID = currentRegistrant
          ? currentRegistrant.id
          : generateRegistrationID(event.registration as Registration);

        if (updatedRegistration.waitlistRegistrants === undefined) {
          updatedRegistration.waitlistRegistrants = [];
        }
        if (currentRegistrant) {
          const index = updatedRegistration.waitlistRegistrants?.findIndex(
            (val: Registrant) => val.id === currentRegistrant.id
          );
          if (index !== -1 && index !== undefined) {
            (
              updatedRegistration.waitlistRegistrants[index] as Registrant
            ).players = players;
            (
              updatedRegistration.waitlistRegistrants[index] as Registrant
            ).answers = answers;
            (
              updatedRegistration.waitlistRegistrants[index] as Registrant
            ).time = new Date().toISOString();
            setCurrentRegistrant(
              updatedRegistration.waitlistRegistrants[index] as Registrant
            );
          } else {
            const newRegistrant: Registrant = {
              players,
              answers,
              id: registrationID,
              time: new Date().toISOString(),
            };
            updatedRegistration.waitlistRegistrants.push(newRegistrant);
            setCurrentRegistrant(newRegistrant);
          }
        } else {
          const newRegistrant: Registrant = {
            players,
            answers,
            id: registrationID,
            time: new Date().toISOString(),
          };
          updatedRegistration.waitlistRegistrants.push(newRegistrant);
          setCurrentRegistrant(newRegistrant);
        }

        transaction.update(eventRef, 'registration', updatedRegistration);
        return registrationID;
      }
    );
    const playerNames: any = {};
    players.forEach((player, i) => {
      playerNames[
        `player_${alphabet[i]}`
      ] = `${player.firstName} ${player.lastName}`;
      playerNames[`player_${alphabet[i]}_email`] = player.email;
    });
    track({
      channel: 'registration',
      event: 'Add To Waitlist',
      icon: '🫡',
      tags: {
        event_name: event.name,
        event_creator: event.creatorEmail,
        ...playerNames,
      },
    });
    const shouldShowConfetti = !currentRegistrant;
    setShowConfetti(shouldShowConfetti);
    setUpdatingRegistration(false);
    sendConfirmationEmail(returnedRegistrationID, true);
    setUnsavedChanges(false);
  }

  async function register() {
    if (
      answers.some((answer: string, i: number) => {
        const question = event.registration?.questions?.[i];
        if (
          question?.required ||
          question?.type === RegistrationQuestionType.MultipleChoice
        ) {
          return answer === '';
        }
        return false;
      })
    ) {
      toast.error(
        'Please answer all required questions and select an option for all multiple choice questions.'
      );
      return;
    }
    try {
      const returnedRegistrationID = await runTransaction(
        getFirestore(),
        async (transaction) => {
          const eventRef = getEventRef();

          const currentEvent = await transaction.get(eventRef);
          const currentUser = await transaction.get(
            doc(getFirestore(), 'users', event.createdBy)
          );

          const currentRoster = [...(currentUser.data()?.roster ?? [])];

          const updatedRegistration = {
            ...currentEvent.data()?.registration,
          } as Registration;

          const registrationID = currentRegistrant
            ? currentRegistrant.id
            : generateRegistrationID(event.registration as Registration);

          try {
            validateTeeTimeAvailability(updatedRegistration);
          } catch (e: any) {
            toast.error(e.message);
            setIsLoading(false);
            return null;
          }

          if (currentRegistrant) {
            const index = updatedRegistration.registrants.findIndex(
              (val: Registrant) => val.id === currentRegistrant.id
            );
            (updatedRegistration.registrants[index] as Registrant).players =
              players;
            (updatedRegistration.registrants[index] as Registrant).answers =
              answers;
            (updatedRegistration.registrants[index] as Registrant).time =
              new Date().toISOString();
            setCurrentRegistrant(
              updatedRegistration.registrants[index] as Registrant
            );
          } else {
            const newRegistrant: Registrant = {
              players,
              answers: answers.map((val) => val ?? ''),
              id: registrationID,
              time: new Date().toISOString(),
            };
            updatedRegistration.registrants.push(newRegistrant);
            setCurrentRegistrant(newRegistrant);
          }

          transaction.update(eventRef, 'registration', updatedRegistration);

          const updatedRoster = [...currentRoster];
          players.forEach((player) => {
            // First check if player exists with the same GHIN
            let index = -1;

            if (player.ghin) {
              // Case 1: User signs up with a GHIN, find them by GHIN
              index = updatedRoster.findIndex(
                (val: RosterMember) => val.player.ghin === player.ghin
              );

              if (index !== -1) {
                // Update existing user with new info
                (updatedRoster[index] as RosterMember).player.firstName =
                  player.firstName;
                (updatedRoster[index] as RosterMember).player.lastName =
                  player.lastName;
                (updatedRoster[index] as RosterMember).player.email =
                  player.email ?? '';
              } else {
                // Case 2: User with GHIN not found by GHIN, check if exists by name/email
                index = updatedRoster.findIndex(
                  (val: RosterMember) =>
                    val.player.firstName === player.firstName &&
                    val.player.lastName === player.lastName &&
                    val.player.email === player.email
                );

                if (index !== -1) {
                  // User found by name/email, update with GHIN and other info
                  (updatedRoster[index] as RosterMember).player.ghin =
                    player.ghin;
                } else {
                  // User not found at all, add as new
                  updatedRoster.push({ player, clubs: [] });
                }
              }
            } else {
              // Case 3: User without GHIN
              index = updatedRoster.findIndex(
                (val: RosterMember) =>
                  val.player.firstName === player.firstName &&
                  val.player.lastName === player.lastName &&
                  val.player.email === player.email
              );

              if (index !== -1) {
                // User found by name/email, update only if they don't have a GHIN
                if (!(updatedRoster[index] as RosterMember).player.ghin) {
                  // Only update handicap if existing user doesn't have a GHIN
                  (updatedRoster[index] as RosterMember).player.handicap =
                    player.handicap;
                }
                // If they have a GHIN, do nothing (keep existing record)
              } else {
                // User not found at all, add as new
                updatedRoster.push({ player, clubs: [] });
              }
            }
          });
          const userDoc = doc(getFirestore(), 'users', event.createdBy);
          try {
            transaction.update(userDoc, 'roster', updatedRoster);
          } catch (e: any) {
            const message = {
              to: ['matt@livetourney.com, henry@livetourney.com'],
              from: `Registration Error <noreply@livetourney.com>`,
              replyTo: 'matt@livetourney.com',
              message: {
                subject: `Error adding player to roster for ${event.name}.`,
                text: `Some info - During roster add portion: Error message -${e.toString()}. Player info - ${JSON.stringify(
                  preserveUndefined(players)
                )}. Updated Registration info - ${JSON.stringify(
                  preserveUndefined(answers)
                )}. Stack Trace - ${e.stack}`,
              },
            };

            await addDoc(collection(getFirestore(), 'mail'), {
              ...message,
            });
          }
          return registrationID;
        }
      );

      if (returnedRegistrationID == null) {
        return;
      }

      const playerNames: any = {};
      players.forEach((player, i) => {
        playerNames[
          `player_${alphabet[i]}`
        ] = `${player.firstName} ${player.lastName}`;
        playerNames[`player_${alphabet[i]}_email`] = player.email;
      });
      track({
        channel: 'registration',
        event: 'Registration Succeeded',
        icon: '👍',
        tags: {
          event_name: event.name,
          ...playerNames,
          is_update: currentRegistrant !== null,
          event_creator: event.creatorEmail,
        },
      });
      if (!currentRegistrant) {
        // Only email if new registration

        await sendConfirmationEmail(returnedRegistrationID);

        setShowConfetti(true);
      }
      setUpdatingRegistration(false);
      setUnsavedChanges(false);
    } catch (e: any) {
      if (e.message === 'Selected tee time is no longer available.') {
        toast.error(e.message);
      } else {
        const message = {
          to: ['matt@livetourney.com, henry@livetourney.com'],
          from: `Registration Error <noreply@livetourney.com>`,
          replyTo: 'matt@livetourney.com',
          message: {
            subject: `Error registering for ${event.name}.`,
            text: `Some info: Error message -${e.toString()}. Player info - ${JSON.stringify(
              preserveUndefined(players)
            )}. Updated Registration info - ${JSON.stringify(
              preserveUndefined(answers)
            )}. Stack Trace - ${e.stack}`,
          },
        };

        await addDoc(collection(getFirestore(), 'mail'), {
          ...message,
        });
      }
    }
  }

  function getPlayersForTeeTime(teeTime: string) {
    return (
      event.registration?.registrants
        ?.filter((r: Registrant) => {
          const teeTimeQuestionIndex = event.registration?.questions.findIndex(
            (q) => q.type === RegistrationQuestionType.TeeTime
          );
          return r.answers[teeTimeQuestionIndex ?? 0] === teeTime;
        })
        .flatMap((r: Registrant) =>
          r.players.map((p) => `${p.firstName} ${p.lastName}`)
        ) ?? []
    );
  }

  function getRegistrationFields() {
    return (
      <div
        className={`transition-all ${
          players.length === event.playersPerTeam ? 'hidden' : 'inline'
        }`}
      >
        {(event.registration?.clubs?.length ?? 0) > 0 && (
          <div className="w-full">
            <SearchBar
              placeholder="Search Roster"
              roster={availablePlayers}
              addPlayer={async (p: Player) => {
                setEmail(p.email ?? '');
                setFirstName(p.firstName);
                setLastName(p.lastName);
                setGhin((p.ghin ?? '').toString());

                let index = p.handicap.toString() ?? 0;

                if (p.ghin) {
                  const response = await fetch(
                    `${USGA_BASE_URL}/golfers.json?status=Active&from_ghin=true&per_page=25&page=1&golfer_id=${p.ghin}&source=GHIN.com`,
                    {
                      method: 'GET',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${ghinToken}`,
                      },
                    }
                  );

                  // If you care about a response:
                  if (response.body !== null) {
                    const result: any = await response.json();
                    if (
                      !isNullOrUndefined(result?.golfers) &&
                      result.golfers.length > 0
                    ) {
                      if (
                        result?.golfers[0] !== null &&
                        result?.golfers[0] !== undefined
                      ) {
                        index = result?.golfers[0].handicap_index as string;
                        if (index.charAt(0) === '+') {
                          index = `-${index.substring(1)}`;
                        } else if (index === 'NH') {
                          index = '0';
                        }
                      }
                    }
                  }
                }
                setHandicap(index);
              }}
              setPlayers={(_val) => {}}
              selectedPlayers={players}
              canDuplicatePlayers={false}
            />
          </div>
        )}

        <div className="bg-gray-100 w-72 p-2 flex items-center mb-3 mx-auto">
          <FaRegEnvelope className="text-gray-400 m-2" />
          <input
            type="email"
            name="email"
            placeholder="Email"
            className="bg-gray-100 outline-none text-sm flex-1"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
          />
        </div>

        {event.registration?.isNonGolfEvent !== true ? (
          <>
            {!event.registration?.requireGHIN && (
              <div className="my-4">
                <span className="mr-4">Name & Handicap</span>
                <Switch
                  checked={isGHIN}
                  onChange={(val: boolean) => {
                    setIsGHIN(val);
                  }}
                  className={`${
                    isGHIN ? 'bg-primary' : 'bg-tertiary'
                  } relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`transform transition ease-in-out duration-200 ${
                      isGHIN ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white`}
                  />
                </Switch>
                <span className="ml-4">GHIN</span>
              </div>
            )}

            {isGHIN ? (
              <div className="bg-gray-100 w-72 p-2 flex items-center mb-3 mx-auto">
                <FaGolfBall className="text-gray-400 m-2" />
                <input
                  type="number"
                  name="ghin"
                  placeholder="Handicap ID (GHIN)"
                  className="bg-gray-100 outline-none text-sm flex-1"
                  onChange={(e) => setGhin(e.target.value)}
                  value={ghin}
                />
              </div>
            ) : (
              <div>
                <div className="bg-gray-100 w-72 p-2 flex items-center mb-3 mx-auto">
                  <FaGolfBall className="text-gray-400 m-2" />
                  <input
                    type="text"
                    name="first"
                    placeholder="First Name"
                    className="bg-gray-100 outline-none text-sm flex-1"
                    onChange={(e) => setFirstName(e.target.value)}
                    value={firstName}
                  />
                </div>
                <div className="bg-gray-100 w-72 p-2 flex items-center mb-3 mx-auto">
                  <FaGolfBall className="text-gray-400 m-2" />
                  <input
                    type="text"
                    name="last"
                    placeholder="Last Name"
                    className="bg-gray-100 outline-none text-sm flex-1"
                    onChange={(e) => setLastName(e.target.value)}
                    value={lastName}
                  />
                </div>
                <div className="bg-gray-100 w-72 p-2 flex items-center mb-3 mx-auto">
                  <FaGolfBall className="text-gray-400 m-2" />
                  <input
                    type="text"
                    name="handicap"
                    placeholder="Handicap Index"
                    className="bg-gray-100 outline-none text-sm flex-1"
                    onChange={(e) => setHandicap(e.target.value)}
                    value={handicap}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            <div className="bg-gray-100 w-72 p-2 flex items-center mb-3 mx-auto">
              <FaGolfBall className="text-gray-400 m-2" />
              <input
                type="text"
                name="first"
                placeholder="First Name"
                className="bg-gray-100 outline-none text-sm flex-1"
                onChange={(e) => setFirstName(e.target.value)}
                value={firstName}
              />
            </div>
            <div className="bg-gray-100 w-72 p-2 flex items-center mb-3 mx-auto">
              <FaGolfBall className="text-gray-400 m-2" />
              <input
                type="text"
                name="last"
                placeholder="Last Name"
                className="bg-gray-100 outline-none text-sm flex-1"
                onChange={(e) => setLastName(e.target.value)}
                value={lastName}
              />
            </div>
          </div>
        )}

        {/* Select price option from registration.prices if they exist */}
        {(event.registration?.prices?.length ?? 0) > 1 ? (
          <div
            className={`bg-gray-100 w-72 p-2 flex items-center mb-3 mx-auto`}
          >
            <BsCash className="text-gray-400 m-2" />
            <select
              className="bg-gray-100 outline-none md:text-sm text-xs flex-1 max-w-[240px] truncate"
              onChange={(e) => {
                const price = event.registration?.prices?.find(
                  (regPrice: RegistrationPrice) =>
                    regPrice.name === e.target.value
                );
                if (price) {
                  setSelectedPrice(price);
                }
              }}
            >
              {event.registration?.prices?.map((price: any, i: number) => {
                return (
                  <option key={i} value={price.name}>
                    {!event.registration?.feePaidByPlayer
                      ? USDollar.format(price.fee)
                      : USDollar.format(
                          (price.fee + 0.3) / stripePercentage
                        )}{' '}
                    - {price.name}
                  </option>
                );
              })}
            </select>
          </div>
        ) : null}
        {/* Select price option from registration.prices if they exist */}

        <div
          onClick={addPlayer}
          className="border-2 border-primary rounded-full px-12 py-2 inline-block text-primary font-semibold hover:bg-primary hover:text-white hover:border-primary hover:cursor-pointer"
        >
          Add {event.registration?.isNonGolfEvent ? 'Registrant' : 'Player'}
        </div>
        <div className="my-2 text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center justify-center w-full flex-1 px-4 md:px-20 text-center">
        <div>
          <Confetti run={showConfetti} recycle={false} />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl flex flex-col md:flex-row w-full lg:w-2/3 max-w-5xl">
          <div
            className={`p-5 transition-width ease duration-600 ${
              players.length === event.playersPerTeam ||
              (currentRegistrant && !updatingRegistration)
                ? 'md:w-2/5'
                : 'md:w-3/5'
            }`}
          >
            <div className="text-left font-bold ">
              <Link href="/" passHref>
                <img alt="logo" className="w-36 md:w-48" src={logo} />
              </Link>
            </div>

            <div className="py-10">
              <h2 className="text-3xl font-bold text-primary">
                {currentRegistrant &&
                !updatingRegistration &&
                (event.registration?.waitlistRegistrants?.length ?? 0) === 0
                  ? `Registration Confirmed for ${event.name}`
                  : currentRegistrant &&
                    !updatingRegistration &&
                    (event.registration?.waitlistRegistrants?.length ?? 0) > 0
                  ? `Waitlist Confirmed for ${event.name}`
                  : registrationOpen
                  ? `Register for ${event.name}`
                  : event.registration?.registrationStart != null &&
                    new Date(event.registration?.registrationStart ?? '') >=
                      new Date()
                  ? `Registration opens soon for ${event.name}`
                  : `Registration is closed for ${event.name}`}
              </h2>
              {event.date != null && event.time != null && (
                <h3 className="text-md text-gray-400 mb-2 tracking-tighter">
                  {/* Show event date and time */}
                  {new Date(
                    formatDateAndTimeToInputFormat(event.date, event.time)
                  ).toLocaleString([], {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </h3>
              )}

              <div className="border-2 w-10 border-primary inline-block mb-2"></div>
              <div className="border-2 w-10 border-primary inline-block mb-2"></div>

              <p className="text-gray-400 my-3">
                <div
                  className={`scorecard-builder w-full h-full`}
                  dangerouslySetInnerHTML={{
                    __html: event.registration?.description ?? '',
                  }}
                ></div>
              </p>

              <div className="flex flex-col items-center mx-auto">
                {(!currentRegistrant || updatingRegistration) &&
                registrationOpen
                  ? getRegistrationFields()
                  : null}
              </div>
              {!registrationOpen && !event.registration?.hasWaitlist && (
                <p className="text-gray-400 my-20 text-lg">
                  Please contact the event organizer for more information.
                </p>
              )}
              {!registrationOpen &&
                event.registration?.hasWaitlist &&
                (event.registration?.registrants
                  ?.filter(
                    (val: Registrant) =>
                      isNullOrUndefined(val.paymentStatus) ||
                      val.paymentStatus === 'paid'
                  )
                  .flatMap((r: Registrant) => r.players).length ?? 0) >=
                  event.registration?.maxPlayerCount && (
                  <div>
                    <p className="text-gray-400 my-8 text-lg">
                      This event is currently full. Please join the waitlist to
                      be notified if a spot becomes available.
                    </p>
                    {getRegistrationFields()}
                  </div>
                )}
              <p className="text-gray-400 mt-3">
                {registrationOpen
                  ? 'Registration closes'
                  : event.registration?.registrationStart != null &&
                    new Date(event.registration?.registrationStart ?? '') >=
                      new Date()
                  ? 'Registration opens'
                  : 'Registration closed'}
                :{' '}
                {event.registration?.registrationStart != null &&
                new Date(event.registration?.registrationStart ?? '') >=
                  new Date()
                  ? new Date(
                      event.registration?.registrationStart ?? ''
                    ).toLocaleString([], {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })
                  : new Date(
                      event.registration?.registrationCutOff ?? ''
                    ).toLocaleString([], {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
              </p>

              <p className="text-gray-400">
                Maximum{' '}
                {event.registration?.isNonGolfEvent ? 'Registrants' : 'Players'}
                : {event.registration?.maxPlayerCount} |{' '}
                {event.registration?.hidePlayersRegistered ? (
                  <span>Currently Registered: {playerCount}</span>
                ) : (
                  <span
                    className={
                      ' text-blue-500 text-center hover:underline hover:cursor-pointer'
                    }
                    onClick={() => {
                      setShowModal(true);
                      setModalTitle('View Registrations');
                      setModalContent(
                        <ViewRegistrations
                          registration={event.registration as Registration}
                          eventCode={event.eventCode}
                          event={event}
                          isAdmin={false}
                        />
                      );
                      setModalButton(DEFUALT_MODAL_BUTTON);
                    }}
                  >
                    Currently Registered: {playerCount}
                  </span>
                )}
              </p>
              {event.registration?.prices?.length === 1 &&
                event.registration.prices[0]?.fee !== 0 && (
                  <p className="text-gray-400">
                    Price:{' '}
                    {!event.registration?.feePaidByPlayer
                      ? USDollar.format(event.registration?.prices[0]?.fee ?? 0)
                      : USDollar.format(
                          ((event.registration?.prices[0]?.fee ?? 0) + 0.3) /
                            stripePercentage
                        )}{' '}
                    Per Player
                  </p>
                )}
            </div>
          </div>
          {registrationOpen ||
          event.registration?.hasWaitlist ||
          currentRegistrant != null ? (
            <div
              className={`bg-primary text-white rounded-b-2xl md:rounded-tr-2xl rounded-br-2xl py-4 md:py-12 px-12 transition-width ease duration-600 ${
                players.length === event.playersPerTeam ||
                (currentRegistrant && !updatingRegistration)
                  ? 'md:w-3/5'
                  : 'md:w-2/5'
              }`}
            >
              <h2 className="text-3xl font-bold mb-2">Your Registration</h2>
              <div className="border-2 w-10 border-white inline-block mb-2"></div>
              {players.length >= 1 ? (
                <div className="flex flex-col items-center">
                  <div className="mb-6">
                    <p>
                      {players.length}{' '}
                      {players.length === 1 ? 'Registrant' : 'Registrants'}{' '}
                      Added
                    </p>
                    {players.length !== event.playersPerTeam &&
                      (event.registration?.requireFullTeam ?? false) &&
                      players.length !== 0 && (
                        <p className="text-white font-bold uppercase text-sm my -6">
                          {`Add ${
                            (event.playersPerTeam ?? 0) - players.length
                          } more
                    ${
                      (event.playersPerTeam ?? 0) - players.length === 1
                        ? 'registrant'
                        : 'registrants'
                    } to complete registration.`}
                        </p>
                      )}
                  </div>
                  {players.map((player: Player, i: number) => {
                    return (
                      <React.Fragment key={i}>
                        <div className="flex items-center">
                          {!currentRegistrant || updatingRegistration ? (
                            <div
                              onClick={() => {
                                const newPlayers = players.filter(
                                  (p) =>
                                    p.ghin !== player.ghin ||
                                    p.firstName !== player.firstName ||
                                    p.lastName !== player.lastName
                                );
                                setPlayers(newPlayers);
                              }}
                              className="border rounded-full w-6 h-6 hover:bg-white hover:text-primary hover:cursor-pointer"
                            >
                              X
                            </div>
                          ) : null}
                          <div className="flex items-center ml-2">
                            {event.registration?.isNonGolfEvent === true
                              ? `${player.firstName} ${player.lastName}`
                              : `${player.firstName} ${player.lastName} - ${
                                  player.handicap < 0
                                    ? `+${player.handicap * -1}`
                                    : player.handicap
                                }`}
                          </div>
                        </div>
                        <div className="mb-4">
                          {(player.registrationPrice?.fee ?? 0) > 0
                            ? `${
                                !event.registration?.feePaidByPlayer
                                  ? USDollar.format(
                                      player.registrationPrice?.fee ?? 0
                                    )
                                  : USDollar.format(
                                      ((player.registrationPrice?.fee ?? 0) +
                                        0.3) /
                                        stripePercentage
                                    )
                              } - ${player.registrationPrice?.name}`
                            : ''}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <p className="mb-10">
                  No{' '}
                  {event.registration?.isNonGolfEvent
                    ? 'Registrants'
                    : 'Players'}{' '}
                  Added
                </p>
              )}
              {event.registration?.questions.map(
                (question: RegistrationQuestion, i: number) => {
                  switch (question.type) {
                    case RegistrationQuestionType.Text:
                      return (
                        <div
                          key={i}
                          className="flex flex-col items-center mt-4"
                        >
                          <label className="block text-md font-medium text-white">
                            {question.question}
                          </label>
                          {currentRegistrant && !updatingRegistration ? (
                            <p>{currentRegistrant?.answers[i]}</p>
                          ) : (
                            <input
                              type="text"
                              name="answer"
                              placeholder={`Answer${
                                question.required ? ' (required)' : ''
                              }`}
                              className="bg-white outline-none text-sm flex-1 text-primary w-64 p-2 flex items-center mb-3"
                              onChange={(e) => {
                                const newAnswers = [...answers];
                                newAnswers[i] = e?.target?.value ?? '';
                                setAnswers(newAnswers);
                              }}
                              value={answers[i]}
                            />
                          )}
                        </div>
                      );
                    case RegistrationQuestionType.MultipleChoice:
                      // Multiple Choice Question
                      return (
                        <div
                          key={i}
                          className="flex flex-col items-center mt-4"
                        >
                          <label className="block text-md font-medium text-white">
                            {question.question}
                          </label>
                          {currentRegistrant && !updatingRegistration ? (
                            <p>{currentRegistrant?.answers[i]}</p>
                          ) : (
                            <select
                              className="bg-white outline-none text-sm flex-1 text-primary w-64 p-2 flex items-center mb-3"
                              onChange={(e) => {
                                const newAnswers = [...answers];
                                newAnswers[i] = e?.target?.value ?? '';
                                setAnswers(newAnswers);
                              }}
                              value={answers[i]}
                            >
                              <option value="">Select an option</option>
                              {question.choices.map(
                                (choice: string, j: number) => {
                                  return (
                                    <option key={j} value={choice}>
                                      {choice}
                                    </option>
                                  );
                                }
                              )}
                            </select>
                          )}
                        </div>
                      );
                    case RegistrationQuestionType.TeeTime:
                      // eslint-disable-next-line no-case-declarations
                      const availableSlots = calculateAvailableSlots(question);
                      return (
                        <div
                          key={i}
                          className="flex flex-col items-center mt-4"
                        >
                          <label className="block text-md font-medium text-white">
                            Tee Time
                          </label>
                          {currentRegistrant && !updatingRegistration ? (
                            <div className="text-center">
                              <p className="font-bold">
                                {currentRegistrant?.answers[i]}
                              </p>
                              <p className="text-sm opacity-80">
                                {getPlayersForTeeTime(
                                  currentRegistrant?.answers[i] ?? ''
                                ).join(', ')}
                              </p>
                            </div>
                          ) : (
                            <div className="relative">
                              <button
                                onClick={() =>
                                  setShowTeeTimeDropdown(!showTeeTimeDropdown)
                                }
                                className="bg-white text-primary w-64 p-2 rounded text-left shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col items-left relative"
                              >
                                {answers[i] ? (
                                  <>
                                    <div className="font-bold">
                                      {answers[i]} -{' '}
                                      {availableSlots[answers[i] ?? ''] === 0
                                        ? 'Full'
                                        : `${
                                            availableSlots[answers[i] ?? '']
                                          } ${
                                            availableSlots[answers[i] ?? ''] ===
                                            1
                                              ? 'slot'
                                              : 'slots'
                                          }`}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      {getPlayersForTeeTime(
                                        answers[i] ?? ''
                                      ).join(', ') ||
                                        'No players registered yet'}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-gray-500">
                                    Select a tee time
                                  </div>
                                )}
                                <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                                  {showTeeTimeDropdown ? (
                                    <FaChevronUp />
                                  ) : (
                                    <FaChevronDown />
                                  )}
                                </div>
                              </button>

                              {showTeeTimeDropdown && (
                                <div className="absolute top-full left-0 w-64 max-h-96 overflow-y-auto bg-white border border-gray-200 rounded-b shadow-lg z-50">
                                  {question.teeTimes?.map((teeTime, j) => {
                                    const playersInSlot = getPlayersForTeeTime(
                                      teeTime.time
                                    );
                                    const isDisabled =
                                      (availableSlots[teeTime.time] ?? 0) <=
                                        0 ||
                                      (availableSlots[teeTime.time] ?? 0) <
                                        players.length;

                                    return (
                                      <div
                                        key={j}
                                        onClick={() => {
                                          if (!isDisabled) {
                                            const newAnswers = [...answers];
                                            newAnswers[i] = teeTime.time;
                                            setAnswers(newAnswers);
                                            setShowTeeTimeDropdown(false);
                                          }
                                        }}
                                        className={`p-3 border-b last:border-b-0  cursor-pointer text-left ${
                                          isDisabled
                                            ? 'opacity-50 cursor-not-allowed'
                                            : ''
                                        } ${
                                          answers[i] === teeTime.time
                                            ? 'bg-gray-300 hover:bg-gray-300'
                                            : 'hover:bg-gray-50'
                                        }`}
                                      >
                                        <div className="font-bold text-primary">
                                          {teeTime.time} -{' '}
                                          {availableSlots[teeTime.time] === 0
                                            ? 'Full'
                                            : `${
                                                availableSlots[teeTime.time]
                                              } ${
                                                availableSlots[teeTime.time] ===
                                                1
                                                  ? 'slot'
                                                  : 'slots'
                                              }`}
                                        </div>
                                        <div className="text-sm text-gray-600">
                                          {playersInSlot.length > 0
                                            ? playersInSlot.join(', ')
                                            : 'No players registered yet'}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    default:
                      return null;
                  }
                }
              )}
              {currentRegistrant && !updatingRegistration ? (
                <div className="flex flex-col">
                  <div
                    onClick={() => setUpdatingRegistration(true)}
                    className="mt-4 border-2 border-white rounded-full px-12 py-2 inline-block text-white font-semibold hover:bg-white hover:text-primary hover:border-primary hover:cursor-pointer"
                  >
                    Update
                  </div>
                  <div
                    onClick={async () => {
                      await cancelRegistration();
                    }}
                    className={`mt-4 border-2 border-white rounded-full px-12 py-2 inline-block text-white font-semibold hover:bg-white hover:text-primary hover:border-primary ${
                      !(event.registration?.requireFullTeam ?? false) ||
                      players.length === event.playersPerTeam
                        ? 'hover:cursor-pointer'
                        : 'hover:cursor-not-allowed'
                    }`}
                  >
                    Cancel Your Registration
                  </div>
                </div>
              ) : players.find(
                  (player) => (player.registrationPrice?.fee ?? 0) > 0
                ) != null ? (
                <a
                  onClick={async () => {
                    if (loading) {
                      return;
                    }
                    setLoading(true);
                    if (
                      !(event.registration?.requireFullTeam ?? false) ||
                      players.length === event.playersPerTeam
                    ) {
                      await collectPayments();
                    }
                    setLoading(false);
                  }}
                  className={`mt-4 text-2xl border-2 border-white rounded-full px-12 py-2 inline-block text-white font-semibold hover:bg-white hover:text-primary hover:border-primary ${
                    (!(event.registration?.requireFullTeam ?? false) &&
                      players.length > 0) ||
                    players.length === event.playersPerTeam
                      ? 'hover:cursor-pointer animate-beat'
                      : 'hover:cursor-not-allowed'
                  }`}
                >
                  {registrationOpen ? 'Continue To Payment' : 'Join Waitlist'}
                </a>
              ) : (!(event.registration?.requireFullTeam ?? false) &&
                  players.length > 0) ||
                players.length === event.playersPerTeam ? (
                <a
                  onClick={async () => {
                    if (loading) {
                      return;
                    }
                    setLoading(true);
                    if (!registrationOpen) {
                      await addToWaitlist();
                      setLoading(false);

                      return;
                    }
                    if (
                      (!(event.registration?.requireFullTeam ?? false) &&
                        players.length > 0) ||
                      players.length === event.playersPerTeam
                    ) {
                      await register();
                    }
                    setLoading(false);
                  }}
                  className={`mt-4 text-2xl border-2 border-white rounded-full px-12 py-2 inline-block text-white font-semibold hover:bg-white hover:text-primary hover:border-primary ${
                    (!(event.registration?.requireFullTeam ?? false) &&
                      players.length > 0) ||
                    players.length === event.playersPerTeam
                      ? 'hover:cursor-pointer animate-beat'
                      : 'hover:cursor-not-allowed'
                  }`}
                >
                  {registrationOpen ? 'Register!' : 'Join Waitlist'}
                </a>
              ) : null}
            </div>
          ) : (
            <div className="md:w-2/5 bg-primary text-white rounded-b-2xl md:rounded-tr-2xl rounded-br-2xl py-4 md:py-36 px-12">
              <h2 className="text-3xl font-bold mb-2">Go Home, Ball!</h2>
              <div className="border-2 w-10 border-white inline-block mb-2"></div>
              <div className="w-full flex justify-center mb-4">
                <img src={'/assets/gifs/Happy.gif'} alt="Happy Gilmore" />
              </div>

              <a
                onClick={() => {
                  router.push('/');
                }}
                className="border-2 border-white rounded-full px-12 py-2 inline-block text-white font-semibold hover:bg-white hover:cursor-pointer hover:text-primary hover:border-primary"
              >
                Go Home
              </a>
            </div>
          )}
        </div>
      </div>
      {showModal ? modal : null}
    </>
  );
}
